import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';

const TYPES = [
  { key: 'reception', label: 'الاستقبال' },
  { key: 'cafeteria', label: 'الكافتيريا' },
  { key: 'toys', label: 'محل الألعاب' },
  { key: 'main', label: 'رئيسية' },
  { key: 'accounting', label: 'المحاسبة' },
  { key: 'other', label: 'أخرى' }
];

function defaultsFrom(cb) {
  return {
    key: cb?.key || '',
    name: cb?.name || '',
    type: cb?.type || 'reception',
    isMain: !!cb?.isMain,
    isActive: cb?.isActive !== false,
    openingBalance: cb?.openingBalance ?? 0,
    notes: cb?.notes || ''
  };
}

function autoKey(name, type) {
  const ascii = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (ascii.length >= 2) return ascii.slice(0, 40);
  const prefix = type || 'cash';
  return `${prefix}_${Date.now().toString(36)}`;
}

export function CashboxForm({ open, cashbox, onClose, onSubmit, submitting }) {
  const isEdit = !!cashbox;
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm({ defaultValues: defaultsFrom(cashbox) });

  useEffect(() => {
    if (open) reset(defaultsFrom(cashbox));
  }, [open, cashbox, reset]);

  if (!open) return null;

  const submit = (values) => {
    if (!values.name?.trim()) return;
    const body = {
      name: values.name.trim(),
      type: values.type,
      isMain: !!values.isMain,
      isActive: !!values.isActive,
      notes: values.notes || ''
    };
    if (!isEdit) {
      body.openingBalance = Number(values.openingBalance) || 0;
      body.key = values.key?.trim() || autoKey(values.name, values.type);
    }
    onSubmit?.(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="card w-[520px] max-w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">
            {isEdit ? 'تعديل القاصة' : 'قاصة جديدة'}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم *</label>
            <input
              className={clsx('input', errors.name && 'border-red')}
              placeholder="مثلاً: قاصة الاستقبال"
              {...register('name', { required: true })}
            />
          </div>

          {!isEdit ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">
                المعرّف الفريد (key)
                <span className="text-ink-3 font-normal mr-1">— يُولَّد تلقائياً إذا تُرك فارغاً</span>
              </label>
              <input
                className={clsx('input', errors.key && 'border-red')}
                placeholder="مثال: reception_main"
                dir="ltr"
                {...register('key', {
                  pattern: { value: /^[a-z0-9_-]*$/, message: 'أحرف لاتينية صغيرة وأرقام و _ - فقط' }
                })}
              />
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">النوع</label>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <div className="flex flex-wrap gap-1.5">
                  {TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => field.onChange(t.key)}
                      className={clsx(
                        'px-3 py-1.5 rounded-[10px] text-[12px] font-semibold border transition-colors',
                        field.value === t.key
                          ? 'bg-orange text-white border-orange'
                          : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          {!isEdit ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">رصيد افتتاحي (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                {...register('openingBalance', { valueAsNumber: true })}
              />
            </div>
          ) : null}

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
            <textarea
              className="input"
              rows={2}
              {...register('notes')}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
              <input type="checkbox" className="accent-orange" {...register('isMain')} />
              رئيسية
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
              <input type="checkbox" className="accent-orange" {...register('isActive')} />
              فعّالة
            </label>
          </div>
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء'}
          </button>
        </div>
      </form>
    </div>
  );
}
